/**
 * Scrolls the Properties Panel's scroll container so the right section is visible at the top,
 * without ever touching section open/closed state (see `usePropertiesPanelSections`) or opening a
 * closed section just to make scrolling easier.
 *
 * Three independent triggers:
 *  - A direct user action opens a section (accordion header click, keyboard shortcut, nav-icon
 *    click, an imperative "expand and open") — call `notifySectionOpenedByUser(id)` right after;
 *    that section scrolls to the top unconditionally, bypassing the Layout/Effects pin below.
 *  - The user collapses a section with its header — call `notifySectionClosedByUser(id)` right
 *    after; the section right below it comes to the top (see
 *    `resolvePropertiesPanelScrollTarget`'s step 0).
 *  - The current selection OR the open/closed state changes for any other reason (e.g. the user
 *    closes a section) — resolved via `resolvePropertiesPanelScrollTarget`, which prefers the
 *    selection's own open section, then a pinned-open Layout/Effects section (see that module's
 *    doc comment), and never auto-opens a
 *    closed section, falling back to the topmost open one or the panel top. This applies the same
 *    way whether or not anything is selected, so closing a section during a no-selection browsing
 *    session lands on whatever else is open instead of jumping back to the panel top.
 *
 * The effect below only re-runs when `openSectionIds`/`pinnedSectionId`/`selectionKey`/
 * `selectionDefaultSectionId`/`isActive` actually change value — `openSectionIds` is the same
 * memoized array `usePropertiesPanelSections` returns, which only gets a new reference on a real
 * open/close change, so this never fires on every render, keystroke, or property edit.
 */
import { type RefObject, useCallback, useEffect, useRef } from 'react'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'
import type { PropertiesPanelDefaultSectionId } from '@/lib/panels/types/properties-panel-default-section-id'
import { resolvePropertiesPanelScrollTarget } from '@/lib/panels/properties-panel-scroll-target'

export interface UsePropertiesPanelScrollInput {
  /** False while the panel showing Object/Type/Photo/Layout/Effects isn't mounted (another tool
   *  tab is active, or the panel is collapsed) — there is no container/section DOM to scroll. */
  isActive: boolean
  scrollContainerRef: RefObject<HTMLElement | null>
  getSectionElement: (id: PropertiesPanelSectionId) => HTMLElement | null
  openSectionIds: readonly PropertiesPanelSectionId[]
  pinnedSectionId: PropertiesPanelSectionId | null
  /** `null` when nothing is selected. */
  selectionKey: string | null
  selectionDefaultSectionId: PropertiesPanelDefaultSectionId | null
}

export interface UsePropertiesPanelScrollResult {
  notifySectionOpenedByUser: (id: PropertiesPanelSectionId) => void
  notifySectionClosedByUser: (id: PropertiesPanelSectionId) => void
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function scrollToTop(container: HTMLElement): void {
  container.scrollTo?.({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
}

function scrollSectionToTop(container: HTMLElement, section: HTMLElement | null): void {
  if (!section) {
    scrollToTop(container)
    return
  }

  const delta = section.getBoundingClientRect().top - container.getBoundingClientRect().top
  const nextTop = Math.max(0, container.scrollTop + delta)

  container.scrollTo?.({ top: nextTop, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
}

/**
 * Finite animations inside `container` running on an element *above* `section` — e.g. an
 * accordion section collapsing (`animate-accordion-up`). Until they finish, `section`'s measured
 * offset still includes the collapsing content's height, so scrolling to it right away overshoots
 * and leaves it partway down the panel once the content is gone. Animations inside `section`
 * itself (its own content expanding) never move its header, so they're ignored.
 */
function layoutAnimationsAbove(container: HTMLElement, section: HTMLElement): Animation[] {
  if (typeof container.getAnimations !== 'function') return []

  return container.getAnimations({ subtree: true }).filter((animation) => {
    const { effect } = animation

    if (!effect || effect.getComputedTiming().endTime === Infinity) return false

    const { target } = effect as KeyframeEffect

    if (!target) return false

    const position = section.compareDocumentPosition(target)

    return (
      (position & Node.DOCUMENT_POSITION_PRECEDING) !== 0 &&
      (position & Node.DOCUMENT_POSITION_CONTAINS) === 0
    )
  })
}

export function usePropertiesPanelScroll({
  isActive,
  scrollContainerRef,
  getSectionElement,
  openSectionIds,
  pinnedSectionId,
  selectionKey,
  selectionDefaultSectionId,
}: UsePropertiesPanelScrollInput): UsePropertiesPanelScrollResult {
  const pendingManualOpenRef = useRef<PropertiesPanelSectionId | null>(null)
  const pendingManualCloseRef = useRef<PropertiesPanelSectionId | null>(null)

  const notifySectionOpenedByUser = useCallback((id: PropertiesPanelSectionId) => {
    pendingManualOpenRef.current = id
  }, [])

  const notifySectionClosedByUser = useCallback((id: PropertiesPanelSectionId) => {
    pendingManualCloseRef.current = id
  }, [])

  useEffect(() => {
    if (!isActive) return

    const container = scrollContainerRef.current

    if (!container) return

    const manualOpenId = pendingManualOpenRef.current

    pendingManualOpenRef.current = null

    const manualCloseId = pendingManualCloseRef.current

    pendingManualCloseRef.current = null

    const isSectionOpen = (id: PropertiesPanelSectionId) => openSectionIds.includes(id)

    let cancelled = false

    // Measures only once every animation that is still shifting the section has finished;
    // a newer run of this effect (cleanup below) supersedes a scroll that's still waiting.
    const scrollToSection = (section: HTMLElement | null) => {
      const pending = section ? layoutAnimationsAbove(container, section) : []

      if (pending.length === 0) {
        scrollSectionToTop(container, section)
        return
      }

      void Promise.all(pending.map((animation) => animation.finished.catch(() => undefined))).then(
        () => {
          if (!cancelled) scrollSectionToTop(container, section)
        }
      )
    }

    const target =
      manualOpenId && isSectionOpen(manualOpenId)
        ? manualOpenId
        : resolvePropertiesPanelScrollTarget({
            selectionDefaultSectionId,
            pinnedSectionId,
            isSectionOpen,
            collapsedSectionId:
              manualCloseId && !isSectionOpen(manualCloseId) ? manualCloseId : null,
          })

    if (target === 'top') {
      scrollToTop(container)
    } else {
      scrollToSection(getSectionElement(target))
    }

    return () => {
      cancelled = true
    }
  }, [
    isActive,
    scrollContainerRef,
    getSectionElement,
    openSectionIds,
    pinnedSectionId,
    selectionKey,
    selectionDefaultSectionId,
  ])

  return { notifySectionOpenedByUser, notifySectionClosedByUser }
}
